const UTM_STORAGE_KEY = 'bc_utm_params'

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'ref'] as const

interface UtmParams {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  ref?: string
}

/**
 * Capture UTM parameters and custom ref from the current URL.
 * Stores them in sessionStorage so they persist across page navigations
 * but are cleared when the tab closes.
 *
 * Call this early in app init — before any router navigation strips query params.
 */
export function captureUtmParams(): void {
  const params = new URLSearchParams(window.location.search)
  const utm: UtmParams = {}

  let hasAny = false
  for (const key of UTM_KEYS) {
    const value = params.get(key)
    if (value) {
      utm[key] = value
      hasAny = true
    }
  }

  if (hasAny) {
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm))
  }
}

/**
 * Get stored UTM params as a flat object suitable for spreading
 * into PostHog event properties.
 */
export function getUtmParams(): UtmParams {
  const stored = sessionStorage.getItem(UTM_STORAGE_KEY)
  if (!stored) return {}
  try {
    return JSON.parse(stored)
  } catch {
    return {}
  }
}

/**
 * Build a compact source string for the backend login `source` field.
 * Returns null if no UTM params are present.
 *
 * Example: "utm_source=resend;utm_campaign=web_launch"
 */
export function getSourceString(): string | null {
  const utm = getUtmParams()
  const parts: string[] = []
  for (const key of UTM_KEYS) {
    if (utm[key]) {
      parts.push(`${key}=${utm[key]}`)
    }
  }
  return parts.length > 0 ? parts.join(';') : null
}
